'use client'

import type { Size } from 'pdp/src/types/FullProduct'
import {
	getSelectedSize,
	getStoredSelectedSize,
} from 'pdp/src/utils/getSelectedInfo/getSelectedInfo'
import { SELECTED_SIZE_ID_KEY } from 'product/constants/constants'
import {
	type Dispatch,
	type SetStateAction,
	createContext,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react'
import { useResponsive } from 'responsive/hooks/useResponsive/useResponsive'
import { setToSessionStorage } from 'utils/webStorage/sessionStorage'

import { useColors } from '../ColorsProvider/ColorsProvider'

export type SizesContextType = {
	isSheetOpen: boolean
	isUniqueSize: boolean
	openSheet: (isOpen: boolean) => void
	select: (size: Size | undefined) => void
	selectedSize?: Size
	hasErrorMessage: boolean
	hasRelatedSizes: boolean
	setHasErrorMessage: Dispatch<SetStateAction<boolean>>
}

type SizesProviderProps = {
	readonly searchParams: Record<string, string>
	readonly children: React.ReactNode
}

export const SizesContext = createContext<SizesContextType | undefined>(
	undefined
)

export function SizesProvider({
	children,
	searchParams,
}: SizesProviderProps): React.ReactNode {
	const { selectedColor, colors } = useColors()
	const hasRelatedSizes = selectedColor.sizes.some(
		(size) => size.relatedProductId
	)
	const [isSheetOpen, setIsSheetOpen] = useState(false)
	const [hasErrorMessage, setHasErrorMessage] = useState(false)
	const { isLessThanLarge, availableScreenSize } = useResponsive()

	const [selectedSize, setSelectedSize] = useState<Size | undefined>(undefined)

	const isUniqueSize = useMemo(
		() => selectedColor.sizes.length === 1,
		[selectedColor]
	)

	const openSheet = (isOpen: boolean): void => {
		setIsSheetOpen(isOpen)
	}

	const selectSize = (size: Size | undefined) => {
		const hasSizeStock = !!size?.available

		if (isLessThanLarge || hasSizeStock) {
			setSelectedSize(size)
		}
	}

	useEffect(() => {
		if (availableScreenSize) {
			const defaultSize = getSelectedSize({
				isLessThanLarge,
				searchParams,
				colors,
				hasRelatedSizes,
			})
			selectSize(defaultSize)
		}
	}, [availableScreenSize, isLessThanLarge])

	useEffect(() => {
		const newSelectedSize = getStoredSelectedSize({
			colors,
			searchParams,
		})

		if (hasErrorMessage) {
			setHasErrorMessage(false)
		}

		selectSize(newSelectedSize)
	}, [selectedColor])

	useEffect(() => {
		if (selectedSize) {
			setToSessionStorage(SELECTED_SIZE_ID_KEY, selectedSize.id)
		}
	}, [selectedSize])

	const value = useMemo(
		() => ({
			isSheetOpen,
			isUniqueSize,
			openSheet,
			selectedSize,
			hasErrorMessage,
			hasRelatedSizes,
			setHasErrorMessage,
			select: setSelectedSize,
		}),
		[isSheetOpen, isUniqueSize, selectedSize, selectedColor, hasErrorMessage]
	)

	return <SizesContext.Provider value={value}>{children}</SizesContext.Provider>
}

/** @throws {Error} If useSizes is used without SizesProvider */
export const useSizes = (): SizesContextType => {
	const context = useContext(SizesContext)
	if (!context) {
		throw new Error('useSizes must be used within a SizesProvider')
	}
	return context
}
